import { Component, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Document } from 'app/models/dto/document.model';
import { environment } from 'environments/environment';
import { ClearDealDocumentStatus, ReviewDealDocument } from 'app/store/deal/deal-documents/deal-document.actions';
import { AdvancedDocumentEditDialogComponent } from 'app/deal/advanced-deal/tabs/advanced-deal-documents/advanced-document-edit-dialog/advanced-document-edit-dialog.component';
import { Business } from 'app/models/documents/deal-documents/business.model';
import { DocumentRejectionDialogComponent } from 'app/deal/advanced-deal/document-requirements/document-rejection-dialog/document-rejection-dialog.component';
import { DealDocumentsService } from 'app/services/deals/deal-documents.service';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';

interface DocumentCellParams extends ICellRendererParams {
    dealId?: number;
    borrowerId?: number;
    currentUserInstitutionId?: number;
    business?: Business;
    loanProductCategory?: string;
}
@Component({
  selector: 'grid-dynamic-document-cell',
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
  ],
  templateUrl: './dynamic-document-cell.component.html',
  standalone: true,
})
export class DynamicDocumentCellComponent implements ICellRendererAngularComp, AfterViewInit, OnDestroy, OnInit {
    dealId?: number;
    borrowerId?: number;
    currentUserInstitutionId?: number;
    business?: Business;
    loanProductCategory?: string;
    accepted: boolean;
    reviewed: boolean;
    rejected: boolean;
    reviewedUserName?: string;
    acceptedUserName?: string;
    rejectedUserName?: string;
    scanIsClean: boolean;
    mouseOver: boolean;
    canEditAndDelete: boolean;
    params: DocumentCellParams;
    destroyed$ = new Subject<boolean>();
    isTest = this._store.selectSignal(LenderUserState.isTest);
    mouseOverLock: boolean;
    private rowElement!: HTMLElement;

    constructor(
        private _store: Store,
        private dialog: MatDialog,
        private dealDocumentsService: DealDocumentsService
    ) {
    }

    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.mouseOver = false;
        this.rejected = this.params.data.status?.rejected;
        this.accepted = this.params.data.status?.accepted;
        this.reviewed = this.params.data.status?.reviewed;
        this.reviewedUserName = this.params.data.status?.reviewedUser
            ? `${this.params.data.status?.reviewedUser.first} ${this.params.data.status?.reviewedUser.last}`
            : '';
        this.acceptedUserName = this.params.data.status?.acceptedUser
            ? `${this.params.data.status?.acceptedUser.first} ${this.params.data.status?.acceptedUser.last}`
            : '';
        this.rejectedUserName = this.params.data.status?.rejectedUser
            ? `${this.params.data.status?.rejectedUser.first} ${this.params.data.status?.rejectedUser.last}`
            : '';
        this.scanIsClean = this.params.data.scanStatus === 'CLEAN' || this.isTest();
    }

    ngAfterViewInit() {
        // Get the row element after the view is initialized
        this.rowElement = this.params.eGridCell.parentElement as HTMLElement;

        // Add event listeners to the row element
        this.rowElement.addEventListener('mouseover', this.onRowMouseOver.bind(this));
        this.rowElement.addEventListener('mouseout', this.onRowMouseOut.bind(this));
    }

    ngOnInit() {
        this.dealId = this.params.dealId;
        this.borrowerId = this.params.borrowerId;
        this.business = this.params.business;
        this.loanProductCategory = this.params.loanProductCategory;
    }
    /**
     * Ng lifecycle
     * @return void
     */
    ngOnDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    value(): string {
        return this.params.value;
    }

    refresh(params: ICellRendererParams) {
        return false;
    }

    onRowMouseOver() {
        this.mouseOver = true;
    }

    onRowMouseOut(event) {
        if (!this.rowElement.contains(event.relatedTarget)) {
            this.mouseOver = false;
            this.rowElement.classList.remove('ag-row-hover');
        }
    }

    handleReview(event) {
        event.stopPropagation();
        const { data } = this.params;

        if (this.dealId) {
            if (this.reviewed) {
                this._store.dispatch(new ClearDealDocumentStatus(this.dealId, data.id));
            } else {
                this._store.dispatch(new ReviewDealDocument(this.dealId, data.id));
            }
        }

        this.rejected = false;
        this.reviewed = !this.reviewed;
    }

    handleReject(event) {
        event.stopPropagation();
        const { data } = this.params;

        if (this.dealId) {
            this.openRejectionDialog(data);
        }
    }

    handleDownload(event) {
        event?.stopPropagation();
        const { data } = this.params;
        this.downloadDoc(data);
    }

    downloadDoc(doc: Document): void {
        this.dealDocumentsService
            .downloadDocumentResponse(doc.id)
            .subscribe({next: (response: any) => {
                const rawContentType: string = response.headers.get('content-type') || 'application/octet-stream';
                const contentType: string = rawContentType.split(';')[0].trim();
                const disposition: string = response.headers.get('content-disposition') || '';

                // Prefer server-provided filename when present
                let filename = doc.filename || 'document';
                const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
                if (match) {
                    const headerName = decodeURIComponent(match[1] || match[2] || '');
                    if (headerName) {
                        filename = headerName;
                    }
                } else {
                    // No filename header; correct extension from content-type
                    const expectedExt = this.extensionFromMime(contentType);
                    if (expectedExt) {
                        const lower = filename.toLowerCase();
                        if (!lower.endsWith(expectedExt)) {
                            const base = filename.replace(/\.[^.]*$/, '');
                            filename = `${base}${expectedExt}`;
                        }
                    }
                }

                const body: ArrayBuffer = response.body as ArrayBuffer;
                if (!body || (response.headers.get('content-length') === '0')) {
                    // Attempt inline open to surface any server-rendered error
                    window.open(`${environment.API_URL}/document/${doc.id}?stream=1`, '_blank');
                    return;
                }

                const blob = new Blob([body], { type: contentType });
                const fileUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = fileUrl;
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                URL.revokeObjectURL(fileUrl);
                document.body.removeChild(link);
            },
            error: (error) => {
                console.error('Error downloading document:', error);
            }});
    }

    handleEdit(event) {
        event?.stopPropagation();
        const { data } = this.params;
        this.openEditDialog(data);
    }

    openEditDialog(document: Document): void {
        const dialogRef = this.dialog.open(AdvancedDocumentEditDialogComponent, {
            width: '372px',
            disableClose: true,
            data: {
                document,
                dealId: this.dealId
            },
        });

        dialogRef.componentInstance.handleSave.subscribe({
            next: () => {
                const message = 'Changes have been saved.';
                this.accepted = false;
                this.rejected = !this.rejected;
            }
        });
    }

    openRejectionDialog(document: Document): void {
        const dialogRef = this.dialog.open(DocumentRejectionDialogComponent, {
            width: '468px',
            disableClose: false,
            data: {
                dealId: this.dealId,
                document
            },
        });

        dialogRef.componentInstance.handleSave.subscribe({
            next: () => {
                const message = `File rejected for ${this.business?.name}: ${this.loanProductCategory}.`;
                // Update local state immediately for UI responsiveness
                this.rejected = true;
                this.accepted = false;
                this.reviewed = true;
            }
        });
    }

    cellClickHandler(event) {
        event.stopPropagation();
    }

    private extensionFromMime(mime: string): string | null {
        const map: Record<string, string> = {
            'application/pdf': '.pdf',
            'image/png': '.png',
            'image/jpeg': '.jpg',
            'image/gif': '.gif',
            'text/plain': '.txt',
            'text/csv': '.csv',
            'application/json': '.json',
        };
        return map[mime] || null;
    }
}
